// src/pages/CoreAssure/Readiness/Readiness.jsx
//
// CoreAssure ReadyCore (D-048 §4.1) — Day 2-3 MVP page.
//
// Brand: ReadyCore (per CR-2026-CA-READYCORE-RENAME).
// Function: readiness check across 6 of v2 §12.2 categories (drawings,
// materials, personnel, calibration, tooling, procedures), read-only
// against existing SFM tables.
//
// Per-tenant feature flag default OFF for tenant 2 (Chesakl) until Day 30
// of pilot (D-048 §6). Backend 403's the route when flag is false; this
// page does NOT pre-flight the flag — instead it surfaces the 403 message
// to the user as an info banner, which is more informative for support
// triage than hiding the page entirely.

import React, { useEffect, useState, useCallback } from "react";
import PageGuide from "../../../components/PageGuide";
import { useSelector } from "react-redux";
import {
  Container, Stack, Typography, Card, CardContent, Grid,
  Select, MenuItem, FormControl, InputLabel, Button,
  Breadcrumbs, Box, CircularProgress, Alert, Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import ApiCall from "../../../utils/apicall";
import { readinessApi } from "../../../utils/coreassure/readinessApi";
import ReadinessScoreCard from "../../../components/CoreAssure/Readiness/ReadinessScoreCard";
import ReadinessCategoryGrid from "../../../components/CoreAssure/Readiness/ReadinessCategoryGrid";

export default function Readiness() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const isAuthed = !!(user && (user.token || user.accessToken));
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [score, setScore] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingScore, setLoadingScore] = useState(false);
  const [previewDisabled, setPreviewDisabled] = useState(false);
  const [error, setError] = useState(null);

  // Load the job list (tenant-scoped server-side per existing convention).
  useEffect(() => {
    if (!isAuthed) return; // CR-2026-CA-READINESS-AUTH-RACE 2026-05-10: defer until auth context ready
    let cancelled = false;
    setLoadingJobs(true);
    ApiCall.get("/job")
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.data || res?.data || [];
        setJobs(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err.message || "Failed to load jobs";
        setError(msg);
        toast.error(msg);
      })
      .finally(() => { if (!cancelled) setLoadingJobs(false); });
    return () => { cancelled = true; };
  }, [isAuthed]);

  const computeScore = useCallback(async () => {
    if (!selectedJobId) return;
    setLoadingScore(true);
    setError(null);
    setPreviewDisabled(false);
    try {
      const res = await readinessApi.scoreForJob(selectedJobId);
      const envelope = res?.data?.data || null;
      setScore(envelope);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setPreviewDisabled(true);
        setScore(null);
      } else if (status === 404) {
        setError("Job not found in your tenant.");
      } else {
        const msg = err?.response?.data?.error || err.message || "Failed to compute readiness score";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setLoadingScore(false);
    }
  }, [selectedJobId]);

  return (
    <Container maxWidth="xl">
      <Helmet><title>ReadyCore — Readiness check | SFM</title></Helmet>

      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <Typography color="text.primary">CoreAssure</Typography>
        <Typography color="text.primary">ReadyCore</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">ReadyCore — Readiness check</Typography>
          <Typography variant="body2" color="text.secondary">
            Pre-work readiness across 6 categories (drawings, materials, personnel, calibration, tooling, procedures).
            CoreAssure powered. Read-only preview.
          </Typography>
        </Box>
      </Stack>
      <PageGuide
        why="Check that everything's in place — drawings, materials, people, calibration, tooling, procedures — before work starts."
        who="Supervisors and the QM, confirming a job is ready before work starts."
        what="A pre-work readiness check across six categories for a job."
        how="Review each category's status; green means ready to start."
        when="Before releasing a job to the floor."
        standard="AISC §1.7.0"
      />

      {previewDisabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          ReadyCore preview is not enabled for this tenant. Per D-048 §6 pilot guardrails,
          the per-tenant feature flag <code>coreassure_readiness_enabled</code> is OFF by default.
          Contact your administrator to enable.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }}>
            <FormControl sx={{ minWidth: 280 }}>
              <InputLabel id="readycore-job-label">Job</InputLabel>
              <Select
                labelId="readycore-job-label"
                value={selectedJobId}
                label="Job"
                onChange={(e) => { setSelectedJobId(e.target.value); setScore(null); setPreviewDisabled(false); setError(null); }}
                disabled={loadingJobs}
              >
                {loadingJobs && <MenuItem disabled value="">Loading…</MenuItem>}
                {!loadingJobs && jobs.length === 0 && <MenuItem disabled value="">No jobs available</MenuItem>}
                {jobs.map((j) => (
                  <MenuItem key={j.id} value={j.id}>
                    {j.name || j.job_number || `Job #${j.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Recompute the readiness score. Each computation writes one CA-READY-CHECK row in audit_log (immutable).">
              <span>
                <Button
                  variant="contained"
                  startIcon={loadingScore ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={computeScore}
                  disabled={!selectedJobId || loadingScore}
                >
                  {loadingScore ? "Computing…" : "Compute readiness"}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {score && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <ReadinessScoreCard score={score} />
          </Grid>
          <Grid item xs={12} md={8}>
            <ReadinessCategoryGrid score={score} navigate={navigate} />
          </Grid>
        </Grid>
      )}

      {!score && !loadingScore && !previewDisabled && !error && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Pick a job above and click <b>Compute readiness</b> to see the ReadyCore score and per-category status.
              The check is read-only; nothing is written to job state.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
